/**
 * LayoutPersona - persona for editing the per-floor wall skeleton.
 *
 * Extends FloorplanPersona to inherit the floor-selection plumbing that the
 * WallSketchEditorTool (via FloorplanToolBase) depends on. The Layout persona
 * adds one entry-time behavior: on enable, inspect floors that have an
 * outline but no skeleton, and dispatch a `layout_bootstrap_prompt` event
 * the React layer translates into a ConfirmDialog. If the user confirms,
 * one skeleton is generated per qualifying floor from the rooms that fall
 * inside that floor's outline.
 */

import { FloorplanPersona } from './FloorplanPersona';
import { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import { PointoramaOutline } from '../../../utils/PointoramaOutline';
import { buildLayoutWallSketchForFloor } from '../../../utils/skeleton/buildLayoutWallSketch';
import { resolveFloorOutlineRings, type OutlineRingComponent } from '../../../utils/skeleton/outlineRings';
import { getFloorPlanePositionProperty } from '@pointorama/potree-shared';
import type { AnnotationBlacklist, AnnotationCategory } from '../../../viewer/AnnotationBlacklistStack';

/**
 * The ONLY annotation types the Layout persona shows (panel + viewport +
 * picking): the sketch objects. Everything else — rooms, outlines, openings,
 * points, distances, areas, boxes, planes, clusters, surfaces, structures — is
 * hidden. Floors are groups (not annotations) and stay visible. Expressed as an
 * allowlist (whitelist), so any future annotation type is hidden by default.
 * Read by the left panel (persona-aware filtering) and the viewport blacklist.
 */
export const LAYOUT_SKETCH_ANNOTATION_TYPES: ReadonlySet<AnnotationCategory> = new Set(['WALL_SKETCH', 'OPENING_SKETCH', 'SKETCH_OBJECT']);

interface FloorNeedingSkeleton {
  groupIdentifier: string;
  floorHeight: number;
  /** Outline rings (merged sketch outline first, legacy annotation fallback). */
  rings: OutlineRingComponent[];
  /** Legacy OUTLINE annotation to link in the sketch metadata, when the rings
   *  came from one; null for merged rings. */
  outlineIdentifier: string | null;
}

export class LayoutPersona extends FloorplanPersona {
  private bootstrapPending: FloorNeedingSkeleton[] = [];

  protected onEnable(): void {
    super.onEnable();

    // FloorplanPersona dispatches an `objects_request` on next tick to
    // populate floorGroupsData. Wait one frame after that resolves before
    // scanning, so we have the floors list to cross-reference with outlines.
    setTimeout(() => {
      this.maybePromptBootstrap();
    }, 50);
  }

  protected onDisable(): void {
    this.bootstrapPending = [];
    this.dispatchEvent({ type: 'layout_bootstrap_dismiss' });
    super.onDisable();
    // Leaving the Layout persona must bring every other annotation type back
    // immediately, whatever persona comes next. The base disable path calls
    // `restoreFloorVisibility()`, which re-applies `buildVisibilityBlacklist(null)`
    // — for this persona that's the sketch-type ALLOWLIST (the right baseline
    // while the persona is active, e.g. when the selected floor is deleted),
    // so rooms/outlines/openings would stay hidden until another
    // floorplan-family persona happened to overwrite the filter. Clear the
    // per-view filter outright now that the persona is off.
    for (const view of this.viewer.renderViews) {
      view.viewState?.setAnnotationBlacklist(null);
    }
  }

  /**
   * Layout persona visibility: a pure ALLOWLIST — only sketch objects show,
   * everything else is hidden (panel + viewport + picking). Whitelist mode
   * AND-combines facets, so:
   *  - floor selected → must be on that floor AND be a sketch type
   *  - no floor selected → must be a sketch type (no floor constraint)
   */
  protected override buildVisibilityBlacklist(floorId: string | null): AnnotationBlacklist | null {
    const types = LAYOUT_SKETCH_ANNOTATION_TYPES;
    if (floorId) {
      return { mode: 'whitelist', identifiers: new Set([floorId]), types };
    }
    return { mode: 'whitelist', types };
  }


  /**
   * Scan floors that have rooms but no (non-empty) wall sketch yet and
   * classify them by whether generation is possible:
   *  - floors WITH an outline become bootstrap candidates (the confirm
   *    dialog offers to generate a sketch on each), and
   *  - floors WITHOUT an outline are counted separately — a wall sketch is
   *    generated from the rooms scoped by the floor's outline, so these
   *    floors CANNOT generate. The React dialog surfaces them as a warning
   *    ("draw an outline first") instead of silently skipping them.
   * Fires a `layout_bootstrap_prompt` event when either list is non-empty.
   */
  private maybePromptBootstrap(): void {
    const floors = this.getFloorGroupsData();
    const outlines = this.viewer.scene.outlines as PointoramaOutline[];
    const wallSketches = this.viewer.scene.wallSketches as PointoramaWallSketch[];
    const rooms = this.viewer.scene.rooms;

    const candidates: FloorNeedingSkeleton[] = [];
    let missingOutlineCount = 0;
    for (const floor of floors) {
      const existing = wallSketches.find((s) => s.groupIdentifier === floor.groupIdentifier);
      if (existing && existing.getNodes().length > 0) continue;
      const hasRooms = rooms.some((r) => r.groupIdentifier === floor.groupIdentifier);
      if (!hasRooms) continue; // nothing to generate from either way
      // Merged sketch outline first, legacy OUTLINE annotation fallback.
      const rings = resolveFloorOutlineRings(this.viewer, floor.groupIdentifier);
      if (rings.length === 0) {
        missingOutlineCount++;
        continue;
      }
      const legacyOutline = outlines.find((o) => o.groupIdentifier === floor.groupIdentifier);
      candidates.push({
        groupIdentifier: floor.groupIdentifier,
        floorHeight: floor.floor.height,
        rings,
        outlineIdentifier: legacyOutline?.identifier ?? null,
      });
    }

    if (candidates.length === 0 && missingOutlineCount === 0) return;

    this.bootstrapPending = candidates;
    this.dispatchEvent({
      type: 'layout_bootstrap_prompt',
      floorCount: candidates.length,
      missingOutlineCount,
    });
  }

  /**
   * Called by the React dialog when the user confirms. Generates a skeleton
   * for each pending floor, restricted to rooms inside that floor's outline.
   */
  async runBootstrap(): Promise<void> {
    const pending = this.bootstrapPending;
    this.bootstrapPending = [];
    for (const floor of pending) {
      try {
        await buildLayoutWallSketchForFloor(this.viewer, floor.groupIdentifier, floor.floorHeight, floor.rings, floor.outlineIdentifier);
      } catch (err) {
        console.error(`Layout bootstrap failed for floor ${floor.groupIdentifier}:`, err);
      }
    }
  }

  /** Called by the React dialog when the user cancels. */
  cancelBootstrap(): void {
    this.bootstrapPending = [];
  }

  /**
   * On-demand wall-skeleton generation for a single floor, triggered
   * from the floor's annotation context menu. Resolves the floor's
   * outline and floor height, then runs the same bootstrap path the
   * confirm dialog uses. Returns `true` on success, `false` when the
   * floor has no outline.
   *
   * Reads the floor's Z directly from the floor-plane property store
   * instead of from `floorGroupsData` — the latter only gets
   * populated when the Layout persona is ACTIVE (via its
   * `objects_request` flow), but this method can be called when the
   * persona is enabled-but-inactive from any context menu.
   */
  async generateWallsForFloor(groupIdentifier: string): Promise<boolean> {
    const rings = resolveFloorOutlineRings(this.viewer, groupIdentifier);
    if (rings.length === 0) return false;
    const outlines = this.viewer.scene.outlines as PointoramaOutline[];
    const legacyOutline = outlines.find((o) => o.groupIdentifier === groupIdentifier);
    let floorHeight: number;
    try {
      const z = getFloorPlanePositionProperty(groupIdentifier).value?.z;
      if (typeof z !== 'number') return false;
      floorHeight = z;
    } catch {
      return false;
    }
    try {
      await buildLayoutWallSketchForFloor(this.viewer, groupIdentifier, floorHeight, rings, legacyOutline?.identifier ?? null);
      return true;
    } catch (err) {
      console.error(`Wall generation failed for floor ${groupIdentifier}:`, err);
      return false;
    }
  }
}
